import React, { useEffect, useMemo, useState } from 'react';
import { IPanelHeaderRenderer, IPanelProps, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { ViewFormCommandBar } from '../cr/ViewFormCommandBar';
import { IProject, ICustomerFinancialYear } from '../../types';
import { renderDate } from '../cr/ListRenderers';
import styles from '../../styles/cr.module.scss';
import {
    getCurrentFYStartYear,
    formatFYLabel,
    calculateFinancialsBuckets,
    formatCurrencyOrBlank,
} from '../../utils/financialsHelpers';

interface ProjectFinancialsViewProps {
    entityName?: string;
    showForm?: boolean;
    entityId?: number;
    onClosed?: () => void;
    loadEntity: (entityId: number) => Promise<IProject>;
    customerFinancialYear?: ICustomerFinancialYear;
}

export const ProjectFinancialsView: React.FC<ProjectFinancialsViewProps> = ({
    showForm,
    entityName,
    entityId,
    loadEntity,
    onClosed,
    customerFinancialYear,
}) => {
    const [formData, setFormData] = useState<IProject>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const entity = await loadEntity(entityId);
                setFormData(entity);
                setLoading(false);
            } catch (error) {
                console.error('Error loading entity:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [loadEntity]);

    const headerText = useMemo(() => {
        if (!formData) return entityName;
        return `${entityName} - ${formData.Title}`;
    }, [entityName, formData]);

    const currentFYStartYear = getCurrentFYStartYear(customerFinancialYear);
    const fyCurrentLabel = `${formatFYLabel(currentFYStartYear)}*`;
    const fyPlus1Label = formatFYLabel(currentFYStartYear + 1);
    const fyPlus2Label = formatFYLabel(currentFYStartYear + 2);
    const fyPlus3Label = formatFYLabel(currentFYStartYear + 3);
    const fyPlus4Label = formatFYLabel(currentFYStartYear + 4);

    const buckets = useMemo(
        () => calculateFinancialsBuckets(formData?.PPAppProjectFinancials, currentFYStartYear),
        [formData, currentFYStartYear]
    );

    const costRows: { label: string; value?: number }[] = [
        { label: 'Prev Years', value: buckets.prevYears },
        { label: fyCurrentLabel, value: buckets.fyCurrent },
        { label: fyPlus1Label, value: buckets.fyPlus1 },
        { label: fyPlus2Label, value: buckets.fyPlus2 },
        { label: fyPlus3Label, value: buckets.fyPlus3 },
        { label: fyPlus4Label, value: buckets.fyPlus4 },
        { label: 'Future Costs', value: buckets.futureCosts },
    ];

    return (
        <Panel
            className={styles.cr}
            isOpen={showForm}
            headerText={headerText}
            type={PanelType.medium}
            hasCloseButton={false}
            onRenderHeader={(props: IPanelProps, defaultRender: IPanelHeaderRenderer) =>
                <div style={{ marginBottom: '20px' }} className={styles.panelHeaderText}>{defaultRender(props)}</div>
            }
            onRenderNavigation={() =>
                <ViewFormCommandBar onClose={onClosed} />
            }
        >
            <div className={styles.cr}>
                <CrLoadingOverlay isLoading={loading} opaque={true} />
                {formData && (
                    <div>
                        <div className={styles.viewFormFieldLabel}>Start-End Dates</div>
                        <div className={styles.viewFormFieldData}>
                            {renderDate(formData.StartDate)}&nbsp;&nbsp;&nbsp;&nbsp;{renderDate(formData.EndDate)}
                        </div>

                        <div className={styles.viewFormFieldLabel}>Current Financial Year</div>
                        <div className={styles.viewFormFieldData}>
                            {formatFYLabel(currentFYStartYear)}
                        </div>

                        {costRows.map(row => (
                            <React.Fragment key={row.label}>
                                <div className={styles.viewFormFieldLabel}>{row.label}</div>
                                <div className={styles.viewFormFieldData}>{formatCurrencyOrBlank(row.value) || 'N/A'}</div>
                            </React.Fragment>
                        ))}

                        <div className={styles.viewFormFieldLabel}>Total Project Cost</div>
                        <div className={styles.viewFormFieldData}>{formatCurrencyOrBlank(buckets.totalCosts) || 'N/A'}</div>

                        <div className={styles.viewFormFieldLabel}>Total Benefits</div>
                        <div className={styles.viewFormFieldData}>{formatCurrencyOrBlank(formData.TotalBenefits) || 'N/A'}</div>

                        <div className={styles.viewFormFieldLabel}>Net Present Value</div>
                        <div className={styles.viewFormFieldData}>{formatCurrencyOrBlank(formData.NetPresentValue) || 'N/A'}</div>
                    </div>
                )}
            </div>
        </Panel>
    );
};